import { propertyDetectionService } from "@app/app/services/PropertyDetectionService";
import type { ICart, IReservedBooking } from "@app/booking/types";
import { type IImage } from "@app/common/types";
import type { IProperty } from "@app/property/types";
import { IPropertyGroup } from "@app/propertyGroup/types";
import { UtilsDom } from "@hotelchamp/common";
import { addMonths, format } from "date-fns";

import { shadowRoot } from "../../bootstrapper";
import { config as appConfig } from "../config";

// const encoded = {
//     d: "2023-09-19,2023-09-21", // dates fromDate,toDate
//     br: "2435816bas,2435816br541,2433216b65tr,243581338uts,1,1:0,2:1|243581214esw", // id,packageId,roomId,rateId,adultsCount,childCount,infantCount|extra_1
// };

export interface IResolvedConfiguration {
    propertyId: string | undefined;
    propertyGroupId: string | undefined;
}

/**
 * resolvePropertyId
 * Simple method to resolve property id from global scope
 * @returns string | undefined
 */
export const resolvePropertyId = () => ((window as any)[appConfig.globalVendorNamespace] as any)?.ibe?.propertyId;

/**
 * resolvePropertyGroupId
 * Simple method to resolve property group id from global scope
 * @returns string | undefined
 */
export const resolvePropertyGroupId = () => ((window as any)[appConfig.globalVendorNamespace] as any)?.ibe?.propertyGroupId;

export const detectProperty = (propertyGroupToParse: IPropertyGroup) => {
    const currentUrl = window.location.href;

    propertyDetectionService.setPropertyGroup(propertyGroupToParse);

    return propertyDetectionService.resolveProperty(currentUrl);
};

/**
 * resolveIbeConfiguration
 * Simple method to resolve the configuration
 * @see - resolvePropertyId
 * @see - resolvePropertyGroupId
 * Will throw an Error once property could not be resolved
 * @returns
 */
export const resolveIbeConfiguration = (): IResolvedConfiguration => {
    const propertyId = resolvePropertyId();
    const propertyGroupId = resolvePropertyGroupId();

    if (!propertyId && !propertyGroupId && !window.Cypress) {
        throw new Error("Could not resolve propertyId OR propertyGroupId");
    }

    return {
        propertyId,
        propertyGroupId,
    };
};

export interface IStateToUrlParamsOptions {
    whitelist?: string[];
}

export const calendarRange = {
    fromDate: format(new Date(), "yyyy-MM-dd"),
    toDate: format(addMonths(new Date(), 6), "yyyy-MM-dd"),
};

interface IPreloadPropertyImagesOptions {
    primaryOnly?: boolean;
    mode?: "all" | "package" | "room";
}

export const preloadPropertyImages = (property: IProperty, { primaryOnly = true, mode = "room" }: IPreloadPropertyImagesOptions = {}) => {
    const collectedPrimaryImages: IImage[] = [];
    const preloadContainerElement = shadowRoot;

    const collectImages = (items: IProperty["rooms"] | IProperty["packages"]) => {
        items?.forEach((item) => {
            if (item?.images?.length) {
                if (primaryOnly) {
                    collectedPrimaryImages.push(item.images[0]);
                } else {
                    item?.images.forEach((image) => collectedPrimaryImages.push(image));
                }
            }
        });
    };

    if (["all", "room"].includes(mode)) {
        collectImages(property?.rooms || []);
    }

    if (["all", "package"].includes(mode)) {
        collectImages(property?.packages || []);
    }

    const existingPreloadedImageFilenames = Array.from(document.querySelectorAll<HTMLLinkElement>("hc-ibe-preload-img")).map(
        (link) => link.href
    );

    collectedPrimaryImages.forEach((image) => {
        if (!existingPreloadedImageFilenames.includes(image.path)) {
            UtilsDom.createElement(preloadContainerElement as any, "link", {
                rel: "preload",
                href: image.path,
                as: "image",
                id: `hc-ibe-preload-img-${image.id}`,
                class: "hc-ibe-preload-img",
            });
        }
    });
};

export const reservedBookingToCart = (reservedBooking: IReservedBooking): ICart => ({
    total: String(reservedBooking.total),
    discounted_total: String(reservedBooking.discounted_total),
    fees: reservedBooking.cart_items.filter((item) => item.key.includes("res-fee")),
    taxes: reservedBooking.cart_items.filter((item) => item.key.includes("tax")),
    rooms: reservedBooking.cart_items.filter((item) => item.key.includes("rate")),
    discounts: reservedBooking.cart_items.filter((item) => item.key.includes("promocode")),
    extras: reservedBooking.cart_items.filter((item) => item.key.includes("supplement")),
    refundable: reservedBooking.refundable,
    refund_amount: reservedBooking.refund_amount,
    cancellation_within_window: reservedBooking.cancellation_within_window,
});

export const isIbeParam = (key: string) =>
    ["d", "lg", "cu", "pc", "cc", "br[", "p", "c", "id", "pi", "f[type]", "f[filters]", "fo"].includes(key);

export const mergeSearchParams = (oldParams: URLSearchParams, newParams: URLSearchParams): URLSearchParams => {
    const mergedParams = new URLSearchParams();

    // Add all params from params1 to the mergedParams
    oldParams.forEach((value, key) => {
        // If params2 doesn't have this key and this key is not an ibe param key, retain it from params1
        // If the param is an ibe param and was removed, we don't want to add it again.
        if (!newParams.has(key) && !isIbeParam(key)) {
            mergedParams.append(key, value);
        }
    });

    // Add all params from params2 to the mergedParams, replacing any existing keys
    newParams.forEach((value, key) => {
        // For all ibe params, we want to replace the existing value except for the br param which can have multiple values
        const method = key === "br" ? "append" : "set";

        mergedParams[method](key, value);
    });

    return mergedParams;
};

export const getRouteChildValue = () => new URLSearchParams(window.location.search).get("c");

/**
 * Helper function to determine whether a route page (child) is currently active
 * @param page
 * @returns
 */
export const isRoutePageActive = (page: string) => getRouteChildValue() === page;

export const isOneOfRoutePagesActive = (pages: string[]) => pages.some((page) => isRoutePageActive(page));

export const getIsRoutePageActiveMap = (routeChildren: string[]) => {
    const activeChild = getRouteChildValue();

    return routeChildren.reduce(
        (accumulator, child) => ({
            ...accumulator,
            [child]: activeChild === child,
        }),
        {}
    );
};
